import { postAPI } from '../utils/ApiFunctions';
import MapboxLogic from '../utils/mapboxLogic';
import { convertFirstLetterToUpperCase } from '../utils/FormatFunctions';
import { PLACE } from '../pages/home/constants/index';
import { CARE_PATH_CATEGORY_CODE } from '../constants';
import findBySpecialtyIds from '../constants/FacilityTypeCategoryCodes';
import { StatusCodes } from 'http-status-codes';
import { OUT_NETWORK_TIER, GAC_ONLY, PROVIDER_TYPE_CONVERSION_FPC } from '../constants/index';
import {
    determineFormattedCoverageType,
    getProviderIdentifiers,
    buildDentalOonRequest,
    getDentalAuxData,
    determineMemberFieldsBasedOnCoverage,
    getBillingType,
    determinePartialContentError,
    determineNoOONBenefits,
    determineNoGACPACRates,
    checkForNoOONRatesDisclaimer,
    determineIndemnityNoRatesResponse,
} from '../utils/OrchRequestUtil.js';
import {
    getPlanNetworkCodeForNonProviderProcedure,
    getIdentifiersForNonProviderProcedure,
} from '../utils/PriceServiceUtil.js';
import { allProviderSpecialties } from '../utils/ProviderUtil';
import { getMicrofrontendStargate } from '../utils/microFrontendStargateToken.js';

/**
 * Sends a request to the Cost Orchestrator
 * @param {Object} formValues - the values intialized in context/InitialFormValues.js
 * @param {String} sourceSystem - a value representing the portal from which this instance
 *                                of the frontend is being rendered
 *                                (regardless of being standalone or embedded)
 * @param {Array} filteredProviderData - a list of provider data
 * @param {Boolean} oonEstimate - true if for OON estimate, otherwise (& default) to false
 * @param {Object} memberDetails - the response from EPE benefits service
 * @param {String} correlationId - the correlation ID to be used within this request
 * @param {Boolean} gacOnly - true if gac only, otherwise (& default) to false
 * @returns {Promise} - a promise that should resolve to an {Object} with keys
 *                      ['cpttRequestId', 'member', 'scenarios', 'populationConfig',
 *                          'accumulators', 'serviceLimits']
 */
export const getPrice = (
    formValues,
    sourceSystem = 'epe',
    filteredProviderData,
    oonEstimate = false,
    memberDetails,
    planBenefits,
    correlationId,
    gacOnly = false,
    isPreventive = false,
    relatedCarePath = null,
    facilityIndex = 0
) => {
    return new Promise(async (resolve, reject) => {
        const microFrontendToken = await getMicrofrontendStargate();
        const url = process.env.COST_ORCHESTRATOR_URL;
        const request = buildRequest(
            formValues,
            sourceSystem,
            filteredProviderData,
            oonEstimate,
            memberDetails,
            planBenefits,
            gacOnly,
            isPreventive,
            relatedCarePath,
            facilityIndex
        );

        try {
            await postAPI(url, request, correlationId, sourceSystem, microFrontendToken)
                .then((resp) => {
                    // check for error response from Orchestration Service - log & return
                    if (resp instanceof Error) {
                        if (resp.response.status === StatusCodes.INTERNAL_SERVER_ERROR) {
                            handleInternalServerError(resp, resolve, memberDetails);
                        }
                        if (resp.response.status === StatusCodes.CONFLICT) {
                            handleConflictError(resp, resolve, memberDetails);
                        }
                        if (resp.response.status === StatusCodes.BAD_REQUEST) {
                            handleBadRequestError(resp, resolve);
                        }
                        orchestrationServiceErrorMessage(resolve, resp);
                    }

                    // check for partial content - log & determine whether to return or continue
                    if (resp.status === StatusCodes.PARTIAL_CONTENT) {
                        determinePartialContentError(resp.headers);
                    }

                    const priceServiceResponse = resp.data;

                    resolve(priceServiceResponse);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error) {
            console.error(error);
            resolve([]); // resolves with empty array for nonblocking flow
        }
    });
};

export const buildRequest = (
    formValues,
    sourceSystem = 'epe',
    filteredProviderData,
    oonEstimate = false,
    memberDetails,
    planBenefits,
    gacOnly = false,
    isPreventive,
    relatedCarePath,
    facilityIndex
) => {
    const selectedServiceObj =
        relatedCarePath == null ? formValues.selectedService.value : relatedCarePath;

    const selectedProviderForEstimatesObj = formValues.selectedProvider.value
        ? [formValues.selectedProvider.value]
        : filteredProviderData;

    const selectedZip = MapboxLogic.getMapboxPostCode(formValues.locationOfService.value);

    let allScenarios = [];

    if (!oonEstimate && !gacOnly) {
        allScenarios = buildScenariosForNormalFlow(
            selectedProviderForEstimatesObj,
            formValues,
            isPreventive,
            facilityIndex,
            memberDetails?.popConfig
        );
    } else if (!oonEstimate && gacOnly) {
        allScenarios = buildScenariosForGacOnlyFlow(
            formValues,
            selectedZip,
            memberDetails?.popConfig,
            isPreventive
        );
    } else {
        allScenarios = buildScenariosForOonFlow(
            formValues,
            oonEstimate,
            selectedZip,
            memberDetails?.popConfig,
            isPreventive
        );
    }

    let request = {
        sourcePortal: sourceSystem,
        carePath: {
            id: selectedServiceObj?.fullCarePath?.id,
            name: selectedServiceObj?.fullCarePath?.name,
        },
        member: buildMemberObject(
            memberDetails,
            formValues,
            selectedServiceObj,
            selectedZip,
            planBenefits
        ),
        scenarios: allScenarios,
        populationConfig: memberDetails?.popConfig,
    };

    return request;
};

/**
 * Builds & returns allScenarios object for normal flow
 * @param {*} selectedProviderForEstimatesObj
 * @param {*} formValues
 * @returns allScenarios object
 */

const buildScenariosForNormalFlow = (
    selectedProviderForEstimatesObj,
    formValues,
    isPreventive,
    facilityIndex,
    popConfig
) => {
    const selectedServiceObj = formValues.selectedService.value;
    const selectedServiceForFacilityCharge = selectedServiceObj.facilityChargeInfo;

    const formattedCoverageType = determineFormattedCoverageType(
        selectedServiceObj.fullCarePath.coverageTypeCode
    );

    const { serviceType } = selectedServiceObj.fullCarePath;
    const { serviceTypes } = selectedServiceObj.fullCarePath;

    const selectedZipBackup = MapboxLogic.getMapboxPostCode(formValues.locationOfService.value);

    const allScenarios = [];
    selectedProviderForEstimatesObj.forEach((provider, i) => {
        const claimsArray = [
            buildClaim('1', selectedServiceObj, isPreventive, provider),
            ...(selectedServiceForFacilityCharge
                ? [
                      buildClaim(
                          '2',
                          selectedServiceForFacilityCharge,
                          isPreventive,
                          provider?.facilitiesData?.[facilityIndex],
                          false,
                          selectedZipBackup,
                          popConfig
                      ),
                  ]
                : []), // conditionally adds facilityClaim to the claims array only if the service requires it
        ];

        const scenario = {
            id: (i + 1).toString(),
            coverageType: formattedCoverageType,
            serviceType: serviceType, //TODO: backend expect serviceType to be a string for now so sending both, need to remove after changes are done in the backend
            serviceTypes: serviceTypes,
            claims: claimsArray,
        };
        allScenarios.push(scenario);
    });

    return allScenarios;
};

/**
 * Builds & returns allScenarios object for ONN flow
 * @param {*} formValues
 * @param {*} oonEstimate
 * @param {*} selectedZip - the zipCode these scenarios are being searched against
 * @param {*} popConfig - the popConfig of the member these scenarios are for
 * @returns allScenarios
 */
const buildScenariosForOonFlow = (
    formValues,
    oonEstimate,
    selectedZip,
    popConfig,
    isPreventive
) => {
    const selectedServiceObj = formValues.selectedService.value;
    const carePathCategoryCode = selectedServiceObj.fullCarePath.coverageTypeCode;

    const allScenarios = [
        {
            ...getAllScenariosForOonOrGAC(
                formValues,
                oonEstimate,
                selectedZip,
                popConfig,
                isPreventive
            ),
        },
    ];

    if (carePathCategoryCode == CARE_PATH_CATEGORY_CODE.DENTAL && oonEstimate) {
        allScenarios[0].claims.map((claim) => {
            return claim.procedures.map((procedure) => {
                return buildDentalOonRequest(procedure, selectedServiceObj);
            });
        });
    }

    return allScenarios;
};

/**
 * Builds & returns allScenarios object for GAC only flow
 * @param {*} formValues
 * @returns allScenarios object
 */
const buildScenariosForGacOnlyFlow = (formValues, selectedZip, popConfig, isPreventive) => {
    return [
        { ...getAllScenariosForOonOrGAC(formValues, false, selectedZip, popConfig, isPreventive) },
    ];
};

/**
 * A function that builds the scenarios object for GAC/ONN flow
 * @param {*} formValues
 * @param {*} isOonEstimate
 * @param {*} selectedZip - the zipCode these scenarios are being searched against
 * @param {*} popConfig - the popConfig of the member these scenarios are for
 * @returns Scenarios object
 */
const getAllScenariosForOonOrGAC = (
    formValues,
    isOonEstimate,
    selectedZip,
    popConfig,
    isPreventive
) => {
    const selectedServiceObj = formValues.selectedService.value;
    const selectedServiceForFacilityCharge = selectedServiceObj.facilityChargeInfo;

    const formattedCoverageType = determineFormattedCoverageType(
        selectedServiceObj.fullCarePath.coverageTypeCode
    );

    const { serviceType } = selectedServiceObj.fullCarePath;
    const { serviceTypes } = selectedServiceObj.fullCarePath;

    const claimsArray = [
        buildClaim(
            '1',
            selectedServiceObj,
            isPreventive,
            null,
            isOonEstimate,
            selectedZip,
            popConfig
        ),
        ...(selectedServiceForFacilityCharge
            ? [
                  buildClaim(
                      '2',
                      selectedServiceForFacilityCharge,
                      isPreventive,
                      null,
                      isOonEstimate,
                      selectedZip,
                      popConfig
                  ),
              ]
            : []), // conditionally adds facility claim to the claims array only if selectedServiceForFacilityCharge exists
    ];

    return {
        id: '1',
        coverageType: formattedCoverageType,
        serviceType: serviceType,
        serviceTypes: serviceTypes,
        claims: claimsArray,
    };
};

/**
 *
 * @param {*} id
 * @param {*} selectedServiceObj
 * @param {*} isPreventive - flag to identify if the estimate is preventive or diagnostic
 * @param {*} provider
 * @param {*} oonEstimate
 * @param {*} selectedZip - the zipCode these scenarios are being searched against
 * @param {*} popConfig - the popConfig of the member these scenarios are for
 * @returns
 */
const buildClaim = (
    id,
    selectedServiceObj,
    isPreventive = false,
    provider = null,
    oonEstimate = false,
    selectedZip,
    popConfig
) => {
    return {
        id: id, // just index of this array
        billingType: getBillingType(selectedServiceObj),
        procedures: [
            provider
                ? buildProcedure('1', selectedServiceObj, provider, isPreventive)
                : buildNonProviderProcedure(
                      '1',
                      selectedServiceObj,
                      oonEstimate,
                      selectedZip,
                      popConfig,
                      isPreventive
                  ),
        ],
        isInPatient: selectedServiceObj.carePathStep.category == 'Inpatient',
    };
};

/**
 * Code to find matching place of service code depending specialty codes
 * @param {*} defaultLineItem default lineitem
 * @param {*} provider provider infromation
 * @param {*} carePathStep careoath step information
 * @returns matching placeOFService code
 */
const getMatchingPlaceOfServiceCode = (defaultLineItem, provider, carePathStep) => {
    try {
        const { specialtyCodes } = provider?.locAndPractRole[0];
        let placeOfServiceCode = null;
        if (specialtyCodes) {
            for (let idx = 0; idx < specialtyCodes.length; idx++) {
                const facilityType = findBySpecialtyIds(parseInt(specialtyCodes[idx]));
                if (facilityType) {
                    //instead of iddentifying correct carepathstep agian, we use the already identified carepathstep which we will get as parameter to this func.
                    const { possibleClaims } = carePathStep?.billingInformation;
                    for (let i = 0; i < possibleClaims.length; i++) {
                        if (possibleClaims[i].primaryFacilityTypeCode == facilityType) {
                            placeOfServiceCode = possibleClaims[i].lineItems.find(
                                (lineitem) =>
                                    lineitem?.billingCode?.code ===
                                    defaultLineItem?.billingCode?.code
                            ).placeOfServiceCode?.code;
                            if (placeOfServiceCode) {
                                break;
                            }
                        }
                    }
                    if (placeOfServiceCode) {
                        return placeOfServiceCode;
                    }
                }
            }
        }
        return null;
    } catch (error) {
        console.error(`Error in getMatchingPlaceOfServiceCode: ${error}`);
        return null;
    }
};

const buildProcedure = (id = '1', selectedServiceObj, provider, isPreventive) => {
    const { lineItem, carePathStep, fullCarePath } = selectedServiceObj;
    const derivedPlaceOfServiceCode = getMatchingPlaceOfServiceCode(
        lineItem,
        provider,
        carePathStep
    );
    return {
        id: id, // just index of this array
        isPrimary: true, // TODO hardcoded for now
        ...(lineItem.lengthOfStay && {
            lengthOfStay: lineItem.lengthOfStay,
        }), // if exists, on selectedServiceObj
        procedureCode: {
            ...buildProcedureCodeObject(selectedServiceObj, isPreventive),
            ...(fullCarePath.coverageTypeCode == CARE_PATH_CATEGORY_CODE.DENTAL && {
                dentalAuxData: getDentalAuxData(selectedServiceObj),
            }),
        },
        placeOfServiceCode: derivedPlaceOfServiceCode ?? lineItem?.placeOfServiceCode?.code,
        quantity: lineItem?.quantity,
        diagnosisCode: carePathStep?.diagnosisCode,
        providerType: provider.type ? convertFirstLetterToUpperCase(provider.type) : provider.type,
        pricingTypeCode: lineItem?.pricingType,
        category: carePathStep?.category,
        coverageSubTypeCode: fullCarePath?.coverageSubTypeCode,
        isVirtualVisit: fullCarePath?.hasVirtualVisit,
        duration: carePathStep?.stepDurationDays,
        provider: buildProvider(provider),
        codeModifier: lineItem?.codeModifier,
    };
};

const buildProvider = (provider) => {
    const { npiValue, piddValue, mpinValue, pcpidValue, tinValue, practiceIdValue } =
        getProviderIdentifiers(provider);
    const firstLocAndPract = provider?.locAndPractRole?.[0];
    const tierLevel = firstLocAndPract.tierLevel ?? null; // Setting tierLevel to the value of firstLocAndPract.tierLevel or null if it's undefined

    return {
        identifiers: {
            npi: npiValue,
            pidd: piddValue,
            mpin: mpinValue,
            pcpId: pcpidValue,
            tin: tinValue,
            practiceId: practiceIdValue,
            tinPrefix: null, //just a place holder as of now, to be replaced when we get actual values
            tinSuffix: null, //just a place holder as of now, to be replaced when we get actual values
        },
        ...(provider.type == PLACE && {
            organizationName: provider.name,
        }),
        personName: {
            firstName: provider.name?.first,
            lastName: provider.name?.last,
        },
        providerType: provider.type ? convertFirstLetterToUpperCase(provider.type) : provider.type,
        // TODO **IMPORTANT** we NEED to handle multiple addresses such that
        // when a user selects a provider we EITHER
        // A) need to know which address to use in our pricing estimation OR
        // B) handle providers having more than one location
        providerAddress: {
            streetAddress1: firstLocAndPract?.singleAddress?.line[0],
            zipCode: firstLocAndPract?.singleAddress?.postalCode,
            state: firstLocAndPract?.singleAddress?.state,
            city: firstLocAndPract?.singleAddress?.city,
        },
        isPremiumTier: tierLevel == 'tier1' || tierLevel == 'TIER1',
        tierLevel: tierLevel,
        planNetworkCode: firstLocAndPract?.planNetworkCode ?? '',
        isInNetwork: true,
        providerLocationDdpAttributes: {
            isLabEnabled: provider?.ddpLab || false,
            isImagingCenterEnabled: provider?.ddpImagingCenter || false,
        },
        specialties: allProviderSpecialties(provider?.locAndPractRole),
    };
};

/**
 * this function is used for both OON and INN estimates when no providers are found
 * it attaches a shell provider to the orchestration request in the shape defined by the function
 * @param {number} id - an identifier passed through, the procedure's id
 * @param {object} selectedServiceObj - the specific service level information (i.e. CPT billingCode, etc.)
 * @param {boolean} isOonEstimate - if true, is oon. if false, is inn (or at least NOT oon.)
 * @param {string} selectedZip - the zip the member/advocate is searching for the selectedService in
 * @param {object} popConfig - the popConfig for this member
 * @returns {object} a mocked/shell provider to be sent to the orchestration service for when there were no providers found
 */
const buildNonProviderProcedure = (
    id = '1',
    selectedServiceObj,
    isOonEstimate,
    selectedZip,
    popConfig,
    isPreventive
) => {
    const { lineItem, carePathStep, fullCarePath } = selectedServiceObj;
    const planNetworkCode = getPlanNetworkCodeForNonProviderProcedure(
        popConfig,
        selectedServiceObj,
        isOonEstimate
    );
    const identifiers = getIdentifiersForNonProviderProcedure(selectedServiceObj, popConfig);
    return {
        id: id,
        isPrimary: true, // TODO hardcoded for now
        procedureCode: buildProcedureCodeObject(selectedServiceObj, isPreventive),
        placeOfServiceCode: lineItem?.placeOfServiceCode?.code,
        quantity: lineItem?.quantity,
        diagnosisCode: carePathStep?.diagnosisCode,
        providerType: PROVIDER_TYPE_CONVERSION_FPC[lineItem?.providerType],
        category: carePathStep?.category,
        isVirtualVisit: fullCarePath?.hasVirtualVisit,
        duration: carePathStep?.stepDurationDays,
        provider: {
            /* adding providerType here since pre-d expects this field within the provider */
            providerType: PROVIDER_TYPE_CONVERSION_FPC[lineItem?.providerType],
            providerAddress: {
                // TODO: Once Cost Calc removes streetAddress1 & state as required, we can get rid of these
                streetAddress1: isOonEstimate ? 'oonAddress' : 'innAddress',
                state: isOonEstimate ? 'oonState' : 'innState',
                zipCode: selectedZip,
            },
            tierLevel: isOonEstimate ? OUT_NETWORK_TIER : GAC_ONLY,
            ...(planNetworkCode && { planNetworkCode }),
            isInNetwork: !isOonEstimate,
            ...(identifiers && { identifiers }),
        },
        codeModifier: lineItem?.codeModifier,
    };
};

/**
 * Builds and returns procedureCode object with properties that are same for all flows
 * @param {*} matchedProcedure
 * @returns procedureCode object
 */
const buildProcedureCodeObject = (matchedProcedure, isPreventive) => {
    return {
        code: matchedProcedure?.lineItem?.billingCode?.code,
        codeSystem: matchedProcedure?.lineItem?.billingCode?.system,
        revenueCode: matchedProcedure?.lineItem?.revenueCode?.code,
        revenueSubCode: matchedProcedure?.lineItem?.revenueCode?.code, // TODO revenueCode & subCode may be different but we only have revenueCode from current carePath API data, so we will use that value for both
        hipaaServiceCode: isPreventive
            ? matchedProcedure?.lineItem?.preventativeBenefitCode?.code
            : matchedProcedure?.lineItem.benefitCode?.code,
        drugUnitCount: matchedProcedure?.lineItem?.ndcData?.serviceDrugUnitCount,
        nationalDrugCode: matchedProcedure?.lineItem?.ndcData?.serviceNationalDrugCode,
        drugUnitMeasureCode: matchedProcedure?.lineItem?.ndcData?.serviceDrugUnitMeasureCode,
    };
};

const buildMemberObject = (
    memberDetails,
    formValues,
    selectedServiceObj,
    selectedZip,
    planBenefits
) => {
    const { productCode, eligibilityIdentifier } = determineMemberFieldsBasedOnCoverage(
        memberDetails,
        selectedServiceObj.fullCarePath.coverageTypeCode
    );
    return {
        firstName: memberDetails?.demographics?.name?.firstName,
        lastName: memberDetails?.demographics?.name?.lastName,
        memberId: formValues.memberNumber.value,
        groupNumber: formValues.groupNumber.truncated,
        dateOfBirth: formValues.dateOfBirth.value,
        lineOfBusiness: formValues.lineOfBusiness.value,
        cae: formValues.cae.value,
        sourceCode: formValues.sourceCode.value,
        policyNumber: formValues.policyNumber.value,
        productCode: productCode,
        address: {
            zip: selectedZip,
        },
        userIdentifier: memberDetails?.userIdentifier ?? '',
        eligibilityIdentifier: eligibilityIdentifier,
        tciTableNumber: planBenefits?.tciTableNumber,
        hasDdpLabBenefits: planBenefits?.hasDdpLabBenefits,
        hasDdpRadiologyBenefits: planBenefits?.hasDdpRadiologyBenefits,
        partnerId: memberDetails?.partnerId,
        /* TODO: uncomment & implement once determined if needed for small UMR sub-population
        umrMemberData: {
            employeeOpi: '',
            claimantPolicyNumber: '',
            claimantPolicyClass: '',
            defaultPricingIndicator: '',
        }, */
    };
};

// ERROR HANDLING FUNCTIONS:

/**
 * Function to handle internal server error
 * @param {*} resp
 * @param {*} resolve
 */
const handleInternalServerError = (resp, resolve, memberDetails) => {
    checkAndHandleErrorForIndemnityMember(resolve, resp, memberDetails);
    checkForNoOONRatesDisclaimer(resolve, resp);
    checkForNoGACPACRates(resolve, resp);
    orchestrationServiceErrorMessage(resolve, resp);
};

const handleConflictError = (resp, resolve, memberDetails) => {
    const { hasNoOonBenefits, noOonRatesDisclaimer } = determineNoOONBenefits(resp);
    if (hasNoOonBenefits) {
        resp.response.data = {
            scenarios: [],
            hasNoOONMemberBenefit: hasNoOonBenefits,
            noOONRates: noOonRatesDisclaimer?.text,
        };
        resolve(resp.response.data);
    }
    checkAndHandleErrorForIndemnityMember(resolve, resp, memberDetails);
    checkForNoGACPACRates(resolve, resp);
    orchestrationServiceErrorMessage(resolve, resp);
};

/**
 * Function to handle bad request error
 * @param {*} resp
 * @param {*} resolve
 */
const handleBadRequestError = (resp, resolve) => {
    //Verify OBH member
    if (resp?.response?.data?.member?.sourceCode == 'UB') {
        resolve(resp.response.data);
    }
    orchestrationServiceErrorMessage(resolve, resp);
};

const checkAndHandleErrorForIndemnityMember = (resolve, resp, memberDetails) => {
    const indemnityNoResponse = determineIndemnityNoRatesResponse(resp, memberDetails);
    if (indemnityNoResponse) {
        resp.response.data = {
            scenarios: [],
            accumulators: resp.response.data.accumulators,
            indemnityNoResponse: indemnityNoResponse,
        };
        resolve(resp.response.data);
    }
};

const checkForNoGACPACRates = (resolve, resp) => {
    const noInnGACPACRates = determineNoGACPACRates(resp.response.headers);
    if (noInnGACPACRates.hasNoGACPACRates) {
        const responseDataWithNoGACPAC = { ...resp.response.data, noInnGACPACRates };
        resp.response.data = responseDataWithNoGACPAC;
        resolve(resp.response.data);
    }
};

const orchestrationServiceErrorMessage = (resolve, resp) => {
    console.error(
        `Call to Orchestration Service estimates endpoint failed with error ${resp.message}`
    );
    resolve(resp);
};
